import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { COUNTRY_CODE_US, toCountryCode, type CountryCode } from '@klear/constants/countries'
import { Button } from '@klear/ui/components/button'
import { Combobox, type ComboboxOption } from '@klear/ui/components/combobox'
import { savedCustomerOptions } from '@klear/customer-ui/use-customers'
import { Input } from '@klear/ui/components/input'
import { trpc } from '@klear/ui/lib/trpc'
import { ChoiceGroup } from '@/app/document-review/choice-group'
import { existingOrderEdit, newOrderEdit, type PartyPick } from '@/app/document-review/party-edit'
import { COUNTRY_OPTIONS } from '@/app/document-review/party-picker'
import type { DocumentReviewDetailOutput, DocumentReviewUpdateInput } from '@/app/document-review/use-document-review'

export type OrderEditorProps = {
  detail: DocumentReviewDetailOutput
  traderId: number
  traderName: string
  isPending: boolean
  onSave: (data: DocumentReviewUpdateInput['data']) => void
  onCancel: () => void
}

type OrderSource = 'existing' | 'new'
type CustomerSource = 'existing' | 'create'

function isInPopover(target: EventTarget): boolean {
  return target instanceof HTMLElement && target.closest('[data-radix-popper-content-wrapper]') !== null
}

export function OrderEditor({ detail, traderId, traderName, isPending, onSave, onCancel }: OrderEditorProps) {
  const planned = detail.order
  const plannedCustomer = planned?.kind === 'create' ? planned.customer : null

  const [orderSource, setOrderSource] = useState<OrderSource>(planned?.kind === 'create' ? 'new' : 'existing')
  const [orderId, setOrderId] = useState<number | null>(planned?.kind === 'existing' ? planned.orderId : null)
  const [customerSource, setCustomerSource] = useState<CustomerSource>(
    plannedCustomer?.kind === 'create' ? 'create' : 'existing'
  )
  const [customerId, setCustomerId] = useState<number | null>(
    plannedCustomer?.kind === 'existing' ? plannedCustomer.id : null
  )
  const [name, setName] = useState(plannedCustomer?.kind === 'create' ? plannedCustomer.party.name : '')
  const [countryCode, setCountryCode] = useState<CountryCode>(
    plannedCustomer?.kind === 'create' ? toCountryCode(plannedCustomer.party.countryCode) : COUNTRY_CODE_US
  )

  const orders = trpc.order.list.useQuery({ traderId })
  const customers = trpc.customer.list.useQuery({ traderId }, { enabled: orderSource === 'new' })

  const firstRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    firstRef.current?.focus()
  }, [])

  const orderOptions: ComboboxOption<number>[] = (orders.data ?? []).map((order) => ({
    value: order.id,
    keywords: [order.idDisplay ?? '', order.customerOrganizationName ?? ''],
    label: (
      <span className="flex min-w-0 flex-1 items-baseline justify-between gap-3">
        <span className="font-mono text-xs tabular-nums">{order.idDisplay}</span>
        <span className="text-muted-foreground truncate text-xs">{order.customerOrganizationName}</span>
      </span>
    )
  }))
  const customerOptions: ComboboxOption<number>[] = savedCustomerOptions(customers.data)

  const trimmed = name.trim()
  const chosenCustomer = customers.data?.find(
    (party) => party.identity.kind === 'saved' && party.identity.id === customerId
  )
  const customer: PartyPick | null =
    customerSource === 'existing'
      ? chosenCustomer === undefined || customerId === null
        ? null
        : { kind: 'existing', id: customerId, name: chosenCustomer.name }
      : trimmed === ''
        ? null
        : { kind: 'create', name: trimmed, countryCode }

  const edit =
    orderSource === 'existing'
      ? orderId === null
        ? null
        : existingOrderEdit(orderId)
      : customer === null
        ? null
        : newOrderEdit(customer)
  const canSave = edit !== null && !isPending

  const save = () => {
    if (canSave) onSave(edit)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation()
    if (isInPopover(event.target)) return
    if (event.key === 'Escape') {
      event.preventDefault()
      onCancel()
      return
    }
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      save()
    }
  }

  return (
    <div className="mt-2 flex flex-col gap-3" onKeyDown={onKeyDown}>
      <div className="grid grid-cols-[5.5rem_minmax(0,1fr)] items-center gap-x-3 gap-y-2 text-sm">
        <span className="text-muted-foreground text-xs">Files on</span>
        <div>
          <ChoiceGroup<OrderSource>
            label="Which order the records file on"
            choices={[
              { value: 'existing', label: 'Existing order' },
              { value: 'new', label: 'New order' }
            ]}
            value={orderSource}
            disabled={isPending}
            onChange={setOrderSource}
            selectedRef={orderSource === 'new' ? firstRef : undefined}
          />
        </div>

        {orderSource === 'existing' ? (
          <>
            <span className="text-muted-foreground text-xs">Order</span>
            <Combobox<number>
              ref={firstRef}
              options={orderOptions}
              value={orderId ?? ''}
              onChange={(next) => setOrderId(next === '' ? null : next)}
              valueType="number"
              triggerLabel={orders.isLoading ? 'Loading…' : `Choose one of ${traderName}’s orders…`}
              placeholder="Search orders…"
              disabled={isPending}
              size="sm"
              className="h-8 w-full max-w-80 justify-between"
            />
          </>
        ) : (
          <>
            <span className="text-muted-foreground text-xs">Customer</span>
            <div>
              <ChoiceGroup<CustomerSource>
                label="Whether the customer already exists"
                choices={[
                  { value: 'existing', label: 'Existing customer' },
                  { value: 'create', label: 'New customer' }
                ]}
                value={customerSource}
                disabled={isPending}
                onChange={setCustomerSource}
              />
            </div>
            <span aria-hidden />
            {customerSource === 'existing' ? (
              <Combobox<number>
                options={customerOptions}
                value={customerId ?? ''}
                onChange={(next) => setCustomerId(next === '' ? null : next)}
                valueType="number"
                triggerLabel={customers.isLoading ? 'Loading…' : `Choose one of ${traderName}’s customers…`}
                placeholder="Search customers…"
                disabled={isPending}
                size="sm"
                className="h-8 w-full max-w-80 justify-between"
              />
            ) : (
              <div className="flex max-w-80 gap-2">
                <Input
                  aria-label="Customer name"
                  placeholder="Customer name"
                  containerClassName="min-w-0 flex-1"
                  className="h-8"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') save()
                  }}
                  disabled={isPending}
                />
                <Combobox<CountryCode>
                  aria-label="Country"
                  options={COUNTRY_OPTIONS}
                  value={countryCode}
                  onChange={(next) => {
                    if (next !== '') setCountryCode(next)
                  }}
                  disabled={isPending}
                  size="sm"
                  className="h-8 w-20 shrink-0 justify-between"
                />
              </div>
            )}
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={!canSave} onClick={save}>
          {isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="outline" size="sm" disabled={isPending} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
